import { MetricV7Request } from '@/api-client/data/model';
import { getCategoryTimeAxisInfoByInterval } from '@/common/utils/chartTimeAxis.utils';
import dayjs from 'dayjs';

const PERIOD_UNIT_MAP: Record<string, dayjs.ManipulateType> = {
  s: 'second',
  m: 'minute',
  h: 'hour',
  d: 'day',
};

const getFromTime = (params: { fromTime?: string; toTime?: string; period?: string }) => {
  const { fromTime, toTime, period } = params;
  if (period) {
    const time = +period.slice(1, -1);
    const unit = PERIOD_UNIT_MAP[period.slice(-1)] ?? 'second';

    return dayjs.utc().subtract(time, unit).valueOf();
  }
  if (fromTime && toTime) {
    return dayjs.utc(fromTime).valueOf();
  }
  return 0;
};

/**
 * 응답의 lastTime(=서버 timeRange.toTime echo) 이 미래시일 때 서버 현재시각으로 clamp.
 *
 * OneDayCompare today 요청처럼 toTime 을 "내일 00:00 KST" 미래시로 보내는 경우
 * 서버 응답의 timeRange.toTime 이 그 미래시로 돌아올 수 있다. 이 값을 그대로 다음 폴링의
 * lastTime 으로 쓰면 buffered lastTime 도 미래시가 되어 실제 도착한 데이터를 추적하지 못한다.
 * 차트 라벨 범위를 보존해야 하므로 요청 toTime 은 건드리지 않고, lastTime 값만 클램프한다.
 *
 * @param time 응답에서 추출한 시각 (UTC `YYYY-MM-DD HH:mm:ss`)
 * @param serverNowUtc 서버 기준 현재시각 (UTC `YYYY-MM-DD HH:mm:ss`). 미제공이면 무변경.
 */
export const clampToServerNow = (
  time: string | undefined,
  serverNowUtc: string | undefined,
): string | undefined => {
  if (!time || !serverNowUtc) return time;
  // 두 값의 포맷이 일치하지 않을 가능성(ISO `T` vs 공백 구분자 혼재) 을 고려해
  // 문자열 lex 비교가 아닌 dayjs.utc().valueOf() 로 ms 비교한다.
  const timeMs = dayjs.utc(time).valueOf();
  const serverNowMs = dayjs.utc(serverNowUtc).valueOf();
  // 잘못된 포맷이면 NaN > NaN === false 로 무성 통과되어 미래시가 그대로 흘러갈 수 있다.
  // 비교 불가 시 clamp 자체를 포기(time 그대로) 하되, 결정은 명시적으로 둔다.
  if (Number.isNaN(timeMs) || Number.isNaN(serverNowMs)) return time;
  return timeMs > serverNowMs ? serverNowUtc : time;
};

/**
 * 폴링(반복) 요청에 보간 미적용을 표시한다. 누적 캐시 merge 가 응답 구간을 이어 붙이므로
 * 서버 보간이 끼면 경계 틱이 왜곡된다. 커서(lastTime) 적용 여부와 무관하게 폴링 요청이면
 * 항상 필요해, 커서 적용(applyLastTime — worker)과 분리해 메인 스레드에서 사용한다.
 */
export const markNullInterpolation = (req: MetricV7Request): MetricV7Request => ({
  ...req,
  interpolateType: 'Null',
});

export const applyLastTime = (req: MetricV7Request, lastTime?: string): MetricV7Request => {
  const { interval, fromTime, toTime, period } = req;

  if (!interval || (!period && !(fromTime && toTime)) || !lastTime) {
    return markNullInterpolation(req);
  }

  const { time, unit } = getCategoryTimeAxisInfoByInterval(interval).interval;
  const minTime = getFromTime(req);

  // 3틱 버퍼를 두어 데이터 누락 방지
  const bufferedLastTime = (() => {
    if (lastTime && dayjs.utc(lastTime).valueOf() > minTime) {
      const computed = dayjs.utc(lastTime).subtract(time * 3, unit);

      // bufferedLastTime이 toTime 이상이면 닫힌 윈도이므로 lastTime=toTime으로 설정.
      // 서버가 (lastTime, toTime] 구간 데이터만 반환하도록 하여 full 응답으로 인한 부하를 차단.
      if (toTime && computed.valueOf() >= dayjs.utc(toTime).valueOf()) {
        return toTime;
      }

      return computed.format('YYYY-MM-DD HH:mm:ss');
    }
    return undefined;
  })();

  return {
    ...markNullInterpolation(req),
    lastTime: bufferedLastTime,
  };
};
